import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.applyTitleSortFromUrl();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  applyTitleSortFromUrl() {
    const sortBy = new URLSearchParams(window.location.search).get('sort_by');
    if (sortBy !== 'title-ascending' && sortBy !== 'title-descending') return;

    this.#sortVisibleCardsByTitle(sortBy);
    this.#syncTitleSortControls(sortBy);
  }

  #sortVisibleCardsByTitle(sortBy) {
    const { grid } = this.refs;
    if (!grid) return;

    const direction = sortBy === 'title-descending' ? -1 : 1;
    const cards = Array.from(grid.querySelectorAll('.product-grid__item'));
    cards
      .sort((firstCard, secondCard) => {
        const firstTitle = this.#getCardTitle(firstCard);
        const secondTitle = this.#getCardTitle(secondCard);
        return firstTitle.localeCompare(secondTitle, undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * direction;
      })
      .forEach((card, index) => {
        card.className = card.className.replace(/\bproduct-grid__item--\d+\b/g, '').trim();
        card.classList.add(`product-grid__item--${index}`);
        grid.appendChild(card);
      });
  }

  #getCardTitle(card) {
    return (
      card.getAttribute('data-product-title') ||
      card.querySelector('.product-card__link .visually-hidden')?.textContent ||
      card.textContent ||
      ''
    ).trim();
  }

  #syncTitleSortControls(sortBy) {
    const label = sortBy === 'title-descending' ? 'Alphabetically, Z-A' : 'Alphabetically, A-Z';

    this.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      select.value = sortBy;
    });

    this.querySelectorAll('input[name="sort_by"]').forEach((input) => {
      input.checked = input.value === sortBy;
      input.closest('[role="option"]')?.setAttribute('aria-selected', input.value === sortBy ? 'true' : 'false');
    });

    this.querySelectorAll('sorting-filter-component .facets__status').forEach((status) => {
      status.textContent = label;
    });
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
